import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileText, XCircle } from "lucide-react";
import { Button, EmptyState } from "@finora/ui";
import { ApiError } from "@finora/api-client";
import { api } from "../../lib/api";

const DURATION: Record<string, string> = { UNDEFINED: "neodređeno", MONTHS_12: "12 meseci", MONTHS_24: "24 meseca" };

function money(v: string | number) {
  return Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function PublicOfferPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const { data: offer, isLoading, isError } = useQuery({
    queryKey: ["public-offer", token],
    queryFn: () => api.getPublicOffer(token!),
    enabled: !!token,
  });
  const accept = useMutation({
    mutationFn: () => api.acceptPublicOffer(token!),
    onSuccess: (u) => queryClient.setQueryData(["public-offer", token], u),
  });
  const decline = useMutation({
    mutationFn: () => api.declinePublicOffer(token!),
    onSuccess: (u) => queryClient.setQueryData(["public-offer", token], u),
  });

  return (
    <div className="min-h-screen bg-app-bg py-10">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mb-6 flex items-center gap-2">
          <img src="/brand-logo.png" alt="HAFiskal" className="h-6 w-auto" />
        </div>
        {isLoading ? (
          <p className="text-sm text-slate-500">Učitavanje...</p>
        ) : isError || !offer ? (
          <div className="rounded-xl border border-border bg-surface p-8 shadow-subtle">
            <EmptyState icon={FileText} title="Ponuda nije pronađena" description="Link je možda netačan ili je ponuda uklonjena." />
          </div>
        ) : (
          <div className="space-y-5 rounded-xl border border-border bg-surface p-6 shadow-subtle">
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Ponuda {offer.number}</h1>
              <p className="text-sm text-slate-500">{offer.customerName}</p>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {offer.items.map((i, idx) => (
                  <tr key={idx} className="border-b border-border">
                    <td className="py-2 text-slate-700">{i.name}</td>
                    <td className="py-2 text-right text-slate-700">{money(i.price)} RSD</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-2 font-semibold text-slate-900">
                    Ukupno ({offer.billingPeriod === "YEARLY" ? "godišnje" : "mesečno"}, bez PDV-a)
                  </td>
                  <td className="py-2 text-right font-semibold text-slate-900">{money(offer.total)} RSD</td>
                </tr>
                <tr>
                  <td className="py-2 text-slate-700">PDV {offer.vatPercent}%</td>
                  <td className="py-2 text-right text-slate-700">{money(offer.vatAmount)} RSD</td>
                </tr>
                <tr>
                  <td className="py-2 font-semibold text-slate-900">Ukupno sa PDV-om</td>
                  <td className="py-2 text-right font-semibold text-slate-900">{money(offer.totalWithVat)} RSD</td>
                </tr>
              </tbody>
            </table>
            <p className="text-sm text-slate-600">
              {offer.durationOption !== "NONE" && (
                <>
                  Ugovoreni period: <b>{DURATION[offer.durationOption]}</b>
                </>
              )}
              {offer.validUntil ? `${offer.durationOption !== "NONE" ? " · " : ""}Ponuda važi do ${new Date(offer.validUntil).toLocaleDateString("sr-RS")}` : ""}
            </p>
            {offer.note && <p className="text-sm text-slate-600">{offer.note}</p>}

            {offer.status === "SENT" && (
              <div className="flex gap-3">
                <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
                  <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden /> Prihvatam ponudu
                </Button>
                <Button variant="secondary" disabled={decline.isPending} onClick={() => decline.mutate()}>
                  <XCircle className="mr-1 h-4 w-4" aria-hidden /> Odbijam
                </Button>
              </div>
            )}
            {offer.status === "ACCEPTED" && (
              <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                Ponuda je prihvaćena. Usluga se aktivira, a ugovor i fakturu ćete dobiti od nas.
              </p>
            )}
            {offer.status === "DECLINED" && <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">Ponuda je odbijena.</p>}
            {offer.status === "EXPIRED" && <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">Ponuda je istekla.</p>}
            {(accept.isError || decline.isError) && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                {accept.error instanceof ApiError ? accept.error.response.message : "Akcija nije uspela, pokušajte ponovo."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
